import { FC, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { Button } from '@ebsco-ui/ebsco-ui';

import {
  FeatureFlagDisplay,
  getErrorNotificationProps,
  Icon,
  ModalNotification,
} from '@app/components';
import { useHoldsAndCheckoutsPageConfig } from '@app/hooks';
import {
  getItemStatusPanelMessages,
  itemStatusPanelButtonMessages,
} from '@app/translations';
import {
  Feature,
  holdsAndCheckoutsTabs,
  ItemStatus,
  ItemStatusData,
} from '@app/constants';

import css from '../ItemStatusPanel.module.scss';

interface ItemStatusDataProps {
  itemStatusData: ItemStatusData;
  errorMessage?: string;
  isRemoveHoldError?: boolean;
  onModalOpen: () => void;
  onErrorClose: () => void;
}

export const ItemStatusPanel: FC<ItemStatusDataProps> = ({
  itemStatusData: {
    icon,
    isNonCatalogItem,
    status,
    statusMessage,
    actionButtonText,
    feesTitle,
    feesValue,
    isVirtualItem,
  },
  errorMessage = '',
  isRemoveHoldError,
  onModalOpen,
  onErrorClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { $t } = useIntl();
  const { holdsAndCheckoutsRoute, getTabRoute, selectConfiguredOption } =
    useHoldsAndCheckoutsPageConfig();
  const statusMessages = getItemStatusPanelMessages(selectConfiguredOption);

  const link = getTabRoute(
    status === ItemStatus.onHold
      ? holdsAndCheckoutsTabs.holds
      : holdsAndCheckoutsTabs.checkouts
  );
  const isHoldsAndCheckoutsRoute = location.pathname.startsWith(
    holdsAndCheckoutsRoute
  );
  const isRenew = actionButtonText === $t(itemStatusPanelButtonMessages.renew);

  const handleActionButtonClick = useCallback(() => {
    if (isHoldsAndCheckoutsRoute) {
      onModalOpen();
    } else {
      navigate(link);
    }
  }, [navigate, link, isHoldsAndCheckoutsRoute, onModalOpen]);

  const renderActionButton = () => (
    <div className={css.buttonsWrapper}>
      <Button styleType="link" onClick={handleActionButtonClick}>
        {actionButtonText}
      </Button>
    </div>
  );

  return (
    <div className={css.wrapper} data-auto="status-panel">
      <div className={css.statusWrapper}>
        <Icon icon={icon} className={cn(css.icon, css[icon.iconName])}>
          <div className={css.statusInfo}>
            <span>
              {isNonCatalogItem || isVirtualItem ? (
                <strong data-auto="status-title">
                  {isNonCatalogItem
                    ? $t(statusMessages.feeDue)
                    : $t(statusMessages[status])}
                </strong>
              ) : (
                <>
                  <strong data-auto="status-title">
                    {`${$t(statusMessages[status])}${statusMessage && ': '}`}
                  </strong>
                  {statusMessage && (
                    <span data-auto="status-message">{statusMessage}</span>
                  )}
                </>
              )}
            </span>
            {feesValue && (
              <span>
                <strong data-auto="fee-title">{`${feesTitle}: `}</strong>
                <span data-auto="fee-message">{feesValue}</span>
              </span>
            )}
          </div>
        </Icon>
      </div>
      {!isNonCatalogItem && (
        <FeatureFlagDisplay
          flagToMatch={{
            name: Feature.holdsAndCheckouts,
            isActiveIfNotFound: true,
          }}
        >
          {isRenew ? (
            <FeatureFlagDisplay
              flagToMatch={{
                name: Feature.renewHold,
                isActiveIfNotFound: true,
              }}
            >
              {renderActionButton()}
            </FeatureFlagDisplay>
          ) : (
            renderActionButton()
          )}
        </FeatureFlagDisplay>
      )}
      {isRemoveHoldError && (
        <div className={css.errorWrapper}>
          <ModalNotification
            type="error"
            modalNotifications={{
              error: getErrorNotificationProps({
                title: selectConfiguredOption(
                  <FormattedMessage
                    id="itemStatus.unableToRemoveRequest"
                    defaultMessage="We were unable to remove this request."
                  />,
                  <FormattedMessage
                    id="itemStatus.unableToRemovelHold"
                    defaultMessage="We were unable to remove this hold."
                  />
                ),
                message: errorMessage,
              }),
            }}
            onCloseButtonClick={onErrorClose}
          />
        </div>
      )}
    </div>
  );
};
